import type { TextFormat, VariableMeta } from '@tsagko/label-contracts'

const SINGLE_VARIABLE_RE = /^\s*\{([a-zA-Z][a-zA-Z0-9_]*)\}\s*$/

function formatDate(value: string, pattern: string): string {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  const yyyy = String(parsed.getUTCFullYear()).padStart(4, '0')
  const yy = yyyy.slice(-2)
  const mNum = parsed.getUTCMonth() + 1
  const mm = String(mNum).padStart(2, '0')
  const dd = String(parsed.getUTCDate()).padStart(2, '0')
  const d = String(parsed.getUTCDate())
  const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const monthsLong = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]
  // Single-pass tokenizer: longest tokens first to avoid `MMM`→`May` being
  // re-bitten by a later `M` rule. Anything that doesn't match a token is
  // emitted verbatim.
  const tokenRe = /YYYY|YY|MMMM|MMM|MM|M|DD|D/g
  return pattern.replace(tokenRe, (token): string => {
    switch (token) {
      case 'YYYY':
        return yyyy
      case 'YY':
        return yy
      case 'MMMM':
        return monthsLong[mNum - 1] ?? token
      case 'MMM':
        return monthsShort[mNum - 1] ?? token
      case 'MM':
        return mm
      case 'M':
        return String(mNum)
      case 'DD':
        return dd
      case 'D':
        return d
      default:
        return token
    }
  })
}

function formatNumber(value: string, fmt: Extract<TextFormat, { kind: 'number' }>): string {
  const n = Number(value)
  if (Number.isNaN(n)) return value
  let body =
    fmt.decimals !== undefined ? n.toFixed(fmt.decimals) : String(n)
  if (fmt.separator && fmt.separator !== 'none') {
    const [int, dec] = body.split('.')
    const sep = fmt.separator === 'comma' ? ',' : fmt.separator === 'dot' ? '.' : ' '
    const intPart = int ?? body
    const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, sep)
    body = dec !== undefined ? `${withSep}.${dec}` : withSep
  }
  return body + (fmt.suffix ?? '')
}

function applyFormat(value: string, fmt: TextFormat | undefined): string {
  if (!fmt || fmt.kind === 'plain') return value
  if (fmt.kind === 'date') return formatDate(value, fmt.pattern)
  if (fmt.kind === 'number') return formatNumber(value, fmt)
  return value
}

/**
 * Resolves a TextValue's content string:
 *   - if content is `{name}` (with optional surrounding whitespace), looks up the
 *     variable's value (data → variables.default → '') and applies its format
 *   - otherwise returns the content unchanged (pure literal, partial mid-typing
 *     input like `{`, `{ab`, `pre {x`, or mixed `pre {x} post`)
 *
 * Tolerant by design. `TextValueSchema` rejects malformed content on save, so
 * render-time tolerance is safe and prevents the authoring canvas from
 * crashing while the user is still typing a `{variable}` reference. Mixed
 * content that somehow reaches this function is rendered verbatim — the lint
 * pass and the schema are the right places to surface that as a validation
 * error, not the render path.
 */
export function resolveContent(
  content: string,
  variables: Record<string, VariableMeta>,
  data?: Record<string, unknown>
): string {
  const single = SINGLE_VARIABLE_RE.exec(content)
  if (single) {
    const name = single[1]
    if (name === undefined) {
      // Unreachable: SINGLE_VARIABLE_RE always has a capture group when it matches.
      throw new Error(`resolveContent: regex matched but capture group is undefined`)
    }
    const meta = variables[name]
    let raw: string
    if (data && data[name] !== undefined) raw = String(data[name])
    else raw = meta?.default ?? ''
    return applyFormat(raw, meta?.format)
  }
  return content
}
